import { SUPPORTED_TOOLS } from '../data/pricing';
import { AuditTool, PricingSnapshot, PricingChange, ToolSnapshot, PlanSnapshot } from '../types';

export const pricingService = {
  /**
   * Retrieves the current pricing data source of truth.
   */
  getCurrentPricing(): AuditTool[] {
    return SUPPORTED_TOOLS;
  },

  /**
   * Serializes current or provided pricing data into a structured PricingSnapshot.
   */
  serializePricingSnapshot(tools: AuditTool[] = SUPPORTED_TOOLS): PricingSnapshot {
    const verifiedAt = tools[0]?.plans[0]?.verifiedAt || new Date().toISOString().split('T')[0];
    
    const serializedTools: ToolSnapshot[] = tools.map(tool => ({
      id: tool.id,
      name: tool.name,
      plans: tool.plans.map(plan => ({
        id: plan.id,
        label: plan.label,
        monthlyPrice: plan.monthlyPrice,
        pricingModel: plan.pricingModel,
      })),
    }));

    const version = this.getPricingHashFromTools(serializedTools);

    return {
      version,
      verifiedAt,
      tools: serializedTools,
    };
  },

  /**
   * Generates a stable, simple hash of the tool snapshot array to act as a version identifier.
   */
  getPricingHashFromTools(tools: ToolSnapshot[]): string {
    const serialized = JSON.stringify(tools);
    let hash = 0;
    for (let i = 0; i < serialized.length; i++) {
      const char = serialized.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  },

  /**
   * Generates a hash from a full snapshot.
   */
  getPricingHash(snapshot: PricingSnapshot): string {
    return this.getPricingHashFromTools(snapshot.tools);
  },

  /**
   * Compares an older pricing snapshot with a newer one (or the current one) to identify plan/price differences.
   */
  computePricingChanges(oldSnapshot: PricingSnapshot, newSnapshot: PricingSnapshot): PricingChange[] {
    const changes: PricingChange[] = [];

    // Helper map of new snapshot for fast lookup
    const newToolsMap = new Map<string, ToolSnapshot>();
    for (const tool of newSnapshot.tools) {
      newToolsMap.set(tool.id, tool);
    }

    // 1. Process all tools/plans in the old snapshot
    for (const oldTool of oldSnapshot.tools) {
      const newTool = newToolsMap.get(oldTool.id);

      if (!newTool) {
        // Entire tool was removed in new pricing
        for (const oldPlan of oldTool.plans) {
          changes.push({
            toolId: oldTool.id,
            toolName: oldTool.name,
            planId: oldPlan.id,
            planLabel: oldPlan.label,
            oldPrice: oldPlan.monthlyPrice,
            newPrice: null,
            changeType: 'plan_removed',
          });
        }
        continue;
      }

      // Tool exists, map new plans
      const newPlansMap = new Map<string, PlanSnapshot>();
      for (const plan of newTool.plans) {
        newPlansMap.set(plan.id, plan);
      }

      for (const oldPlan of oldTool.plans) {
        const newPlan = newPlansMap.get(oldPlan.id);

        if (!newPlan) {
          changes.push({
            toolId: oldTool.id,
            toolName: oldTool.name,
            planId: oldPlan.id,
            planLabel: oldPlan.label,
            oldPrice: oldPlan.monthlyPrice,
            newPrice: null,
            changeType: 'plan_removed',
          });
        } else if (oldPlan.monthlyPrice !== newPlan.monthlyPrice) {
          const oldPrice = oldPlan.monthlyPrice;
          const newPrice = newPlan.monthlyPrice;
          
          let changeType: 'price_increased' | 'price_decreased' = 'price_increased';
          if (oldPrice !== null && newPrice !== null && newPrice < oldPrice) {
            changeType = 'price_decreased';
          } else if (oldPrice === null && newPrice !== null) {
            changeType = 'price_increased'; // transitioned from contact-sales (null) to pricing
          } else if (oldPrice !== null && newPrice === null) {
            changeType = 'price_increased'; // transitioned to custom pricing (null)
          }

          changes.push({
            toolId: oldTool.id,
            toolName: oldTool.name,
            planId: oldPlan.id,
            planLabel: oldPlan.label,
            oldPrice,
            newPrice,
            changeType,
          });
        }
      }
    }

    // 2. Identify newly added tools/plans in the new snapshot
    const oldToolsMap = new Map<string, ToolSnapshot>();
    for (const tool of oldSnapshot.tools) {
      oldToolsMap.set(tool.id, tool);
    }

    for (const newTool of newSnapshot.tools) {
      const oldTool = oldToolsMap.get(newTool.id);

      if (!oldTool) {
        // Entirely new tool added
        for (const newPlan of newTool.plans) {
          changes.push({
            toolId: newTool.id,
            toolName: newTool.name,
            planId: newPlan.id,
            planLabel: newPlan.label,
            oldPrice: null,
            newPrice: newPlan.monthlyPrice,
            changeType: 'plan_added',
          });
        }
        continue;
      }

      // Check if plans were added inside existing tool
      const oldPlansMap = new Map<string, PlanSnapshot>();
      for (const plan of oldTool.plans) {
        oldPlansMap.set(plan.id, plan);
      }

      for (const newPlan of newTool.plans) {
        if (!oldPlansMap.has(newPlan.id)) {
          changes.push({
            toolId: newTool.id,
            toolName: newTool.name,
            planId: newPlan.id,
            planLabel: newPlan.label,
            oldPrice: null,
            newPrice: newPlan.monthlyPrice,
            changeType: 'plan_added',
          });
        }
      }
    }

    return changes;
  }
};
